import React, { FC, useState, useRef } from 'react';
import translate from '@translate';
import ProductsSelect from 'src/components/ProductsSelect';
import { checkFormValidity } from 'src/utils/formFunctions';
import { ReceivingProductType } from 'src/types/receiving';
import TextField from 'src/components/TextField';
import DateTimePicker from 'src/components/DateTimePicker';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import { FormErrorsType } from '../../types/global';

export type EditKeysType = 'PRODUCT_ID' | 'QUANTITY' | 'LOT' | 'EXPIRATION_DATE';

const initData: ReceivingProductType = {
    LINE_STATE: 'E',
};
type NewProductBlockPropsType = {
    data?: ReceivingProductType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
};

const NewProductBlock: FC<NewProductBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<ReceivingProductType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const changeValue = (key: EditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        if (key === 'PRODUCT_ID') {
            newData.PRODUCT_ID = value?.ID;
            newData.NAME = value?.NAME;
            newData.SKU = value?.SKU;
            newData.DESCRIPTION = value?.DESCRIPTION;
            newData.EAN = value?.EAN;
            newData.EXP_METHOD = value?.EXP_METHOD;
            newData.PRODUCT_TYPE = value?.PRODUCT_TYPE;
            newData.PRODUCT = value;
        } else {
            newData[key] = value;
        }
        const newErrors = { ...editErrors };
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const newId =
                Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.RECEIVINGS_PRODUCTS_ID)]) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    RECEIVINGS_PRODUCTS_ID: newId,
                },
            ]);

            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography gutterBottom variant="h3" sx={props.editErrors.PRODUCTS ? { color: colors.alert_O } : {}}>
                {translate('receivingDetail.addProduct')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={4}>
                        <ProductsSelect
                            value={editData.PRODUCT_ID}
                            error={editErrors.PRODUCT_ID}
                            idKey="ID"
                            onChange={(value, error) => changeValue('PRODUCT_ID', value, error)}
                            name="PRODUCT_ID"
                            label={translate('receivingDetail.productTable.name')}
                            extendsData={['DESCRIPTION', 'EAN', 'EXP_METHOD', 'PRODUCT_TYPE']}
                            fullWidth
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.QUANTITY}
                            error={editErrors.QUANTITY}
                            onChange={(value, error) => changeValue('QUANTITY', value, error)}
                            name="QUANTITY"
                            label={translate('receivingDetail.productTable.quantity')}
                            fullWidth
                            required
                            type="numberLocale"
                            min={1}
                            step="1"
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.LOT}
                            error={editErrors.LOT}
                            onChange={(value, error) => changeValue('LOT', value, error)}
                            name="LOT"
                            label={translate('receivingDetail.productTable.lot')}
                            fullWidth
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <DateTimePicker
                            value={editData.EXPIRATION_DATE}
                            error={editErrors.EXPIRATION_DATE}
                            onChange={(value, error) => changeValue('EXPIRATION_DATE', value, error)}
                            name={'EXPIRATION_DATE'}
                            label={translate('receivingDetail.productTable.expirationDate')}
                            fullWidth
                            disabled={props.disableEdit}
                            required={editData.EXP_METHOD === 'FEFO'}
                            type="date"
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit}
                            sx={{ marginTop: sizes.input_top_PD }}
                            mobileFullWidht
                        >
                            + {translate('receivingDetail.productTable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewProductBlock;
